/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { Box, Divider, Grid, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import { DrawerFitted } from 'app/components/Common/Drawer/Drawer';
import { IAtAGlance, ILastVerifiedValues, IOrderDetails } from 'app/models/Orders/OrderDetails';
import { GlanceFieldTitle, GlanceFieldValue } from '../OrderDetailsDesign';
import { useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import { convertToPST } from 'app/helpers/dateHelper';
import { atAGlanceSection2, atAGlanceSection3, atAGlanceSection4, atAGlanceSection5, CONSTANT } from 'app/constants';
import { useTranslation } from 'react-i18next';
import { GlanceValue } from '../OrderAtAGlance/GlanceValue';
import { customAddWeek, shouldClearEndDateForPlacementLength } from '../helper';
import _ from 'lodash';
import { InstructionText } from 'app/components/Unit/UnitDesign';
import { selectOrderDetailsUIView } from 'app/components/Order/Store/Order.selectors';
import { getExtensionDuration } from 'app/helpers/getExtensionDuration';
import moment from 'moment';

interface OrderSidePanelProps {
  open: boolean;
  closeDrawer: (event: any) => void;
  atAGlanceDetails: IAtAGlance;
  updateLastVerified(lastVerified: ILastVerifiedValues): void;
  lastVerifiedUpdateStatus: boolean;
  handleOrderUpdate: (property: string, value: any) => void;
  positionsAvailable: number;
  positionsConstant: number;
  editedOrder: IOrderDetails;
  disableEdit: boolean;
}

const useStyles = makeStyles()({
  sideDrawer: {
    height: '100vh',
  },
  innerSectionsContainer: {
    paddingTop: 15,
    maxHeight: '88%',
    overflow: 'auto',
  },
  tileWrapper: {
    borderBottom: '1px solid #E5E8EB',
  },
  titleBar: {
    height: 53,
    paddingLeft: 20,
    paddingTop: 15,
    fontSize: 16,
    fontWeight: 500,
  },
  divider: {
    marginBottom: 20,
    marginRight: 15,
    marginLeft: 15,
  },
  closeIcon: {
    color: '#888888',
  },
  outerWrapper: {
    padding: '0px 5px',
  },
  closeIconWrapper: {
    paddingTop: 3,
  },
  InlineWarningMessageContainer: {
    padding: '0 0.5rem 0 1rem',
    'line-height': '1',
    'margin-bottom': '0.5rem',
  },
});

export const OrderSidePanel = (props: OrderSidePanelProps) => {
  const {
    open,
    closeDrawer,
    atAGlanceDetails,
    lastVerifiedUpdateStatus,
    handleOrderUpdate,
    positionsAvailable,
    positionsConstant,
    editedOrder,
    disableEdit,
  } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const userData = useSelector(selectUser);
  const [glanceDetails, setGlanceDetails] = useState<IAtAGlance>(atAGlanceDetails);
  const [expandFileOut, setExpandFileOut] = useState<boolean>(false);
  const [isPositionDecrementReasonNotValid, setPositionDecrementReasonNotValid] = useState<boolean>(false);
  const [expandDeclineFiles, setExpandDeclineFiles] = useState<boolean>(glanceDetails['positionsAvailable'] === 0);
  const userInfo = {
    userId: userData.userInfo?.employeeId,
    userName: userData.userInfo?.firstName + ' ' + userData.userInfo?.lastName,
  };
  const [fileOutReasonControlEnabed, setFileOutReasonControlEnabed] = useState<boolean>(true);
  const [glanceValueKey, setGlanceValueKey] = useState(1000);
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);

  useEffect(() => {
    setFileOutReasonControlEnabed(positionsAvailable < positionsConstant);
    setExpandFileOut(positionsAvailable < positionsConstant ? true : false);
  }, [positionsAvailable, positionsConstant]);

  const handleOrderChange = (property: string, value: any) => {
    if (property === 'orderDecrementReasonID' && value) {
      setPositionDecrementReasonNotValid(false);
    }
    if (property === 'positionsAvailable') {
      setPositionDecrementReasonNotValid(false);
      if (value === 0 || isNaN(value)) {
        setFileOutReasonControlEnabed(value < positionsConstant);
        setExpandDeclineFiles(true);
        setExpandFileOut(true);
        const glanceDetailsTemp = _.cloneDeep(glanceDetails);
        glanceDetailsTemp.declineFiles = false; //Keep Files field selected in UI
        glanceDetailsTemp.orderDecrementReasonID = null;
        setGlanceDetails({ ...glanceDetailsTemp, positionsAvailable: value });
      } else if (value < positionsConstant) {
        setFileOutReasonControlEnabed(true);
        setExpandDeclineFiles(false);
        setExpandFileOut(true);
        const glanceDetailsTemp = _.cloneDeep(glanceDetails);
        glanceDetailsTemp.declineFiles = true;
        glanceDetailsTemp.orderDecrementReasonID = null;
        setGlanceDetails({ ...glanceDetailsTemp, positionsAvailable: value });
      } else {
        setFileOutReasonControlEnabed(value < positionsConstant);
        setExpandFileOut(value < positionsConstant);
        setExpandDeclineFiles(false);
        const glanceDetailsTemp = _.cloneDeep(glanceDetails);
        glanceDetailsTemp.declineFiles = false;
        glanceDetailsTemp.orderDecrementReasonID = null;
        setGlanceDetails({ ...glanceDetailsTemp, positionsAvailable: value, orderDecrementReasonID: null });
      }
    } else if (property === 'startDate' && value !== 'Invalid Date' && value !== null) {
      const startDateMovedPastEnd =
        glanceDetails.endDate &&
        glanceDetails.endDate !== '-' &&
        new Date(value) > new Date(glanceDetails.endDate);

      if (startDateMovedPastEnd) {
        setGlanceDetails({
          ...glanceDetails,
          startDate: value,
          endDate: '-',
          placementLength: '',
        });
        handleOrderUpdate('endDate', null);
      } else if (glanceDetails.placementLength) {
        const numWeeks =
          glanceDetails.endDate && value !== undefined && value !== null
            ? `${getExtensionDuration(value, new Date(glanceDetails.endDate))}`
            : glanceDetails.placementLength;
        setGlanceDetails({
          ...glanceDetails,
          startDate: value,
          placementLength: numWeeks,
        });
      } else {
        setGlanceDetails({
          ...glanceDetails,
          startDate: value,
          placementLength: '',
        });
      }
      setGlanceValueKey(Math.random() * 10000);
    } else if (property === 'placementLength') {
      if (shouldClearEndDateForPlacementLength(value)) {
        setGlanceDetails({
          ...glanceDetails,
          placementLength: value ?? '',
          endDate: '-',
        });
        handleOrderUpdate('endDate', null);
      } else if (glanceDetails.startDate && glanceDetails.startDate !== '-') {
        setGlanceDetails({
          ...glanceDetails,
          placementLength: value,
          endDate: customAddWeek(new Date(glanceDetails.startDate), value)?.toString() ?? '',
        });
      } else {
        setGlanceDetails({ ...glanceDetails, placementLength: value });
      }
      setGlanceValueKey(Math.random() * 10000);
    } else if (property === 'endDate') {
      const numWeeks =
        glanceDetails.startDate !== '-' && value !== undefined && value !== null
          ? getExtensionDuration(moment(glanceDetails.startDate), value)
          : glanceDetails.placementLength;
      setGlanceDetails({
        ...glanceDetails,
        endDate: value,
        placementLength: numWeeks.toString(),
      });
    } else if (property === 'Max Files Sent Reason') {
      setGlanceValueKey(Math.random() * 10000);
    } else if (property === 'declineFiles') {
      const glanceDetailsTemp = _.cloneDeep(glanceDetails);
      glanceDetailsTemp.declineFiles = value;
      setGlanceDetails(glanceDetailsTemp);
    } else {
      setGlanceDetails({ ...glanceDetails, [property]: value });
    }
    handleOrderUpdate(property, value);
  };

  useEffect(() => {
    if (lastVerifiedUpdateStatus) {
      const verifiedDate = convertToPST(new Date());
      setGlanceDetails({
        ...glanceDetails,
        dateVerified: verifiedDate ? verifiedDate : '',
        verifiedBy: userInfo.userName,
        declineFiles: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVerifiedUpdateStatus]);

  const OrderReason = [
    { id: 1, reason: 'Backfill' },
    { id: 2, reason: 'Competitor Backfill' },
    { id: 3, reason: 'New Order' },
    { id: 4, reason: 'Request for Proposal' },
  ];

  const selectedOrderResosn =
    glanceDetails.orderReason && glanceDetails.orderReason > 0 && glanceDetails.orderReason <= 4
      ? OrderReason.filter(x => x.id === glanceDetails.orderReason)[0].reason
      : '';

  const closeGlanceDrawer = e => {
    const isNotValid =
      expandFileOut && atAGlanceSection2?.some(x => x.title === 'Reason*' && glanceDetails[x.value] === null);
    setPositionDecrementReasonNotValid(isNotValid);
    if (!isNotValid) {
      closeDrawer(e);
    }
  };

  return (
    <DrawerFitted open={open} onDrawerClose={closeGlanceDrawer} width={400}>
      <Grid container direction="column" className={classes.sideDrawer}>
        <Grid item className={classes.tileWrapper}>
          <Grid container direction="row">
            <Grid item xs={10} className={classes.titleBar}>
              {t('order.orderDetails.orderDetailsView.atAGlance.sectionTitle1')}
            </Grid>
            <Grid item xs={2} className={classes.closeIconWrapper}>
              <IconButton
                color="primary"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                disabled={isPositionDecrementReasonNotValid}
                classes={{ root: classes.closeIcon }}
                onClick={closeGlanceDrawer}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Box className={classes.innerSectionsContainer}>
          <Grid item>
            <Grid container className={classes.outerWrapper}>
              {atAGlanceSection2.map((glanceValues, key) => (
                <>
                  {glanceValues.title === 'File Out*' || glanceValues.title === 'Reason*' ? (
                    <>
                      {expandDeclineFiles && glanceValues.title === 'File Out*' && (
                        <>
                          <Grid item xs={3}>
                            <GlanceFieldTitle key={key}>{glanceValues.title}</GlanceFieldTitle>
                          </Grid>
                          <Grid item xs={9} alignItems="center">
                            <GlanceValue
                              title={glanceValues.title}
                              key={key}
                              value={glanceDetails[glanceValues.value]}
                              name={glanceValues.value}
                              editable={glanceValues.editable}
                              editTypeValue={glanceValues.editableType}
                              handleOrderUpdate={handleOrderChange}
                              editedOrder={editedOrder}
                              disableEdit={false}
                            />
                          </Grid>
                        </>
                      )}
                      {expandFileOut && glanceValues.title === 'Reason*' && (
                        <>
                          <Grid item xs={11} style={{ marginBottom: 10 }}>
                            <GlanceValue
                              title={glanceValues.title}
                              key={key}
                              value={glanceDetails[glanceValues.value]}
                              errorField={isPositionDecrementReasonNotValid}
                              name={glanceValues.value}
                              editable={glanceValues.editable}
                              editTypeValue={glanceValues.editableType}
                              handleOrderUpdate={handleOrderChange}
                              editedOrder={editedOrder}
                              disableEdit={!fileOutReasonControlEnabed}
                              hideHelperText={true}
                            />
                            {!fileOutReasonControlEnabed && (
                              <div className={classes.InlineWarningMessageContainer}>
                                <InstructionText>{`You can't modify the file out reason unless new position available value is saved`}</InstructionText>
                              </div>
                            )}
                          </Grid>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Grid item xs={6}>
                        <GlanceFieldTitle key={key}>{glanceValues.title}</GlanceFieldTitle>
                      </Grid>
                      <Grid item xs={6}>
                        {glanceValues.editable && glanceValues.editableType ? (
                          <>
                            <GlanceValue
                                title={glanceValues.title}
                                key={glanceValues.title === 'End Date' ? glanceValueKey + key : key}
                                value={glanceDetails[glanceValues.value]}
                                name={glanceValues.value}
                                editable={glanceValues.editable}
                                editTypeValue={glanceValues.editableType}
                                handleOrderUpdate={handleOrderChange}
                                editedOrder={editedOrder}
                                disableEdit={glanceValues.title === CONSTANT.POSITIONS_AVAILABLE ? false : disableEdit}
                              />
                          </>
                        ) : (
                          <GlanceValue
                            title={glanceValues.title}
                            key={key}
                            value={glanceDetails[glanceValues.value]}
                            name={glanceValues.value}
                            handleOrderUpdate={handleOrderChange}
                            editedOrder={editedOrder}
                            disableEdit={disableEdit}
                          />
                        )}
                      </Grid>
                    </>
                  )}
                </>
              ))}
            </Grid>
            <Divider light className={classes.divider} />
          </Grid>
          <Grid item>
            {atAGlanceSection3.map((glanceValues, key) => (
              <Grid container key={key}>
                <Grid item xs={6}>
                  <GlanceFieldTitle>{glanceValues.title}</GlanceFieldTitle>
                </Grid>
                <Grid item xs={6}>
                  {glanceValues.editable && glanceValues.editableType ? (
                    glanceValues.title === 'Max Files Sent Reason' &&
                    (!editedOrder.maxFilesSent ||
                      glanceDetails.maxFilesSent.toString() === 'No' ||
                      !glanceDetails.maxFilesSent) ? (
                      <GlanceValue
                        title={glanceValues.title}
                        value={glanceDetails[glanceValues.value]}
                        name={glanceValues.value}
                        handleOrderUpdate={handleOrderChange}
                        editedOrder={editedOrder}
                        disableEdit={disableEdit}
                      />
                    ) : (
                      <GlanceValue
                        title={glanceValues.title}
                        value={glanceDetails[glanceValues.value]}
                        name={glanceValues.value}
                        key={glanceValues.title === 'Max Files Sent Reason' ? glanceValueKey + key : key}
                        editable={true}
                        editTypeValue={glanceValues.editableType}
                        handleOrderUpdate={handleOrderChange}
                        editedOrder={editedOrder}
                        disableEdit={disableEdit}
                      />
                    )
                  ) : (
                    <GlanceValue
                      title={glanceValues.title}
                      value={glanceDetails[glanceValues.value]}
                      name={glanceValues.value}
                      handleOrderUpdate={handleOrderChange}
                      editedOrder={editedOrder}
                      disableEdit={disableEdit}
                    />
                  )}
                </Grid>
              </Grid>
            ))}
            <Divider light className={classes.divider} />
          </Grid>
          <Grid item>
            {atAGlanceSection4.map((glanceValues, key) => (
              <Grid container key={key}>
                <Grid item xs={6}>
                  <GlanceFieldTitle>{glanceValues.title}</GlanceFieldTitle>
                </Grid>
                <Grid item xs={6}>
                  <GlanceFieldValue slot={glanceDetails[glanceValues.value]} title={glanceValues.title} key={key}>
                    {glanceDetails[glanceValues.value]}
                  </GlanceFieldValue>
                </Grid>
              </Grid>
            ))}
            <Divider light className={classes.divider} />
          </Grid>
          <Grid item>
            {atAGlanceSection5.map((glanceValues, key) => (
              <Grid container key={key}>
                <Grid item xs={6}>
                  <GlanceFieldTitle>{glanceValues.title}</GlanceFieldTitle>
                </Grid>
                <Grid item xs={6}>
                  {glanceValues.editable && glanceValues.editableType ? (
                    <GlanceValue
                      title={glanceValues.title}
                      value={
                        glanceValues.value === 'orderReason' ? selectedOrderResosn : glanceDetails[glanceValues.value]
                      }
                      name={glanceValues.value}
                      editable={true}
                      editTypeValue={glanceValues.editableType}
                      handleOrderUpdate={handleOrderChange}
                      editedOrder={editedOrder}
                      disableEdit={disableEdit}
                    />
                  ) : (
                    <GlanceValue
                      title={glanceValues.title}
                      value={glanceDetails[glanceValues.value]}
                      name={glanceValues.value}
                      handleOrderUpdate={handleOrderChange}
                      editedOrder={editedOrder}
                      disableEdit={disableEdit}
                    />
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>
    </DrawerFitted>
  );
};
